import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FileText, Plus, Trash2, Download, Upload,
  Play, Square, RotateCcw, Check, ChevronRight, Edit2, X, History as HistoryIcon,
  MoreVertical, Archive, ListPlus, Printer, Camera as CameraIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";
import { queueOp, queueAndFlush, getQueuedDeletedIds, getQueuedCreates, getQueuedOps } from "@/lib/syncQueue";
import { queuePhoto, getQueuedPhotos } from "@/lib/photoQueue";
import "@/lib/photoQueue";
import { API, resolveImageUrl } from "@/lib/api";
import { maskTime, pinCaretEnd } from "@/lib/timeMask";
import { fmtNum } from "@/lib/format";
import { ZoomableImage } from "./ZoomableImage";
import { NoteArea } from "./NoteArea";
import { OrderPalletDetailsDialog } from "./OrderPalletDetailsDialog";
import { OrderHistoryDialog } from "./OrderHistoryDialog";
import { toast } from "sonner";

/** Get pallet items array from an order (backward compat with old single pallet_type_name/quantity) */
function getPalletItems(order) {
  if (order.pallet_items && order.pallet_items.length > 0) return order.pallet_items;
  if (order.pallet_type_name) return [{ pallet_type_name: order.pallet_type_name, quantity: order.quantity || 0 }];
  return [];
}

/** Summary string for collapsed view */
function palletSummary(order) {
  const items = getPalletItems(order);
  if (items.length === 0) return '';
  if (items.length === 1) {
    const i = items[0];
    return `${i.pallet_type_name}${i.quantity > 0 ? ` (${fmtNum(i.quantity)})` : ''}`;
  }
  return items.map(i => `${i.pallet_type_name} (${fmtNum(i.quantity || 0)})`).join(', ');
}

function fmt(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const MAX_TIMER_SECONDS = 60 * 3600; // 60 hours

function getElapsed(base, startedAt) {
  if (startedAt) return Math.min((base || 0) + Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000), MAX_TIMER_SECONDS);
  return Math.min(base || 0, MAX_TIMER_SECONDS);
}

const fmtT = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (isNaN(h)) return "";
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
const openPicker = (e) => { try { e.currentTarget.showPicker?.(); } catch {} };

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]} ${months[d.getMonth()]} ${d.getDate()}`;
}

function TimerRow({ label, secs, running, guys, onStart, onStop, onReset, onGuysChange, onSetTime, disabled, onRename, onRemove, testId }) {
  const [editing, setEditing] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [val, setVal] = useState("");
  const [labelEditing, setLabelEditing] = useState(false);
  const [labelVal, setLabelVal] = useState("");
  const parseTime = (str) => {
    const parts = str.split(':').map(Number);
    if (parts.length === 3) return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    if (parts.length === 2) return (parts[0] || 0) * 60 + (parts[1] || 0);
    return (parts[0] || 0) * 60;
  };
  const save = () => {
    const s = Math.min(parseTime(val), MAX_TIMER_SECONDS);
    if (s >= 0 && onSetTime) onSetTime(s);
    setEditing(false); setVal("");
  };
  const saveLabel = () => {
    if (labelVal.trim() && onRename) onRename(labelVal.trim());
    setLabelEditing(false); setLabelVal("");
  };
  return (
    <div className="py-2" data-testid={testId}>
      {/* Header: timer title */}
      <div className="flex items-center gap-1.5 mb-1.5">
        {labelEditing && !disabled ? (
          <span className="flex items-center gap-1 flex-1 min-w-0">
            <input type="text" value={labelVal} onChange={(e) => setLabelVal(e.target.value)} autoFocus maxLength={60}
              onKeyDown={(e) => { if (e.key === 'Enter') saveLabel(); else if (e.key === 'Escape') { setLabelEditing(false); setLabelVal(""); } }}
              onBlur={saveLabel}
              className="flex-1 min-w-0 max-w-[240px] h-6 text-[10px] uppercase tracking-wider font-bold border border-zinc-300 rounded-none px-1.5 focus:outline-none focus:border-zinc-500"
              data-testid="timer-label-input" />
            <button onMouseDown={(e) => e.preventDefault()} onClick={saveLabel}
              className="p-1 bg-zinc-700 text-white hover:bg-zinc-800 flex-shrink-0" data-testid="timer-label-save">
              <Check className="w-3 h-3" />
            </button>
          </span>
        ) : (
          <span
            className={`text-[11px] uppercase tracking-[0.15em] text-zinc-500 font-bold min-w-0 break-words line-clamp-2 ${!disabled && onRename ? 'cursor-pointer hover:text-zinc-700 hover:underline' : ''}`}
            onClick={() => { if (!disabled && onRename) { setLabelVal(label); setLabelEditing(true); } }}
            title={!disabled && onRename ? "Tap to rename" : undefined}
          >{label}</span>
        )}
        {running && (
          <span className="relative w-1.5 h-1.5 flex-shrink-0">
            <span className="absolute inset-0 rounded-full bg-zinc-400 animate-ping opacity-75" />
            <span className="relative block w-1.5 h-1.5 rounded-full bg-zinc-500" />
          </span>
        )}
      </div>
      {/* Controls: time, play/stop, reset, guys, delete */}
      <div className="flex items-center gap-2">
        {editing && !disabled ? (
          <span className="flex items-center gap-1 flex-shrink-0">
            <input type="text" value={val} onChange={(e) => setVal(maskTime(e.target.value))} autoFocus
              onSelect={pinCaretEnd} inputMode="numeric"
              placeholder="00:00:00"
              onKeyDown={(e) => { if (e.key === 'Enter') save(); else if (e.key === 'Escape') { setEditing(false); setVal(""); } }}
              className="w-20 h-6 text-xs font-mono font-bold text-center border border-zinc-300 rounded-none focus:outline-none focus:border-zinc-500"
              data-testid="timer-edit-input" />
            <button onClick={save} className="p-1 bg-zinc-700 text-white hover:bg-zinc-800" data-testid="timer-edit-save"><Check className="w-3 h-3" /></button>
            <button onClick={() => { setEditing(false); setVal(""); }} className="p-1 text-zinc-400 hover:text-zinc-600"><X className="w-3 h-3" /></button>
          </span>
        ) : (
          <span
            className={`text-base font-mono font-bold text-zinc-700 w-[92px] flex-shrink-0 ${!disabled && onSetTime ? 'cursor-pointer hover:underline' : ''}`}
            onClick={() => { if (!disabled && onSetTime && !running) { setVal(maskTime(fmt(secs))); setEditing(true); } }}
            title={!disabled && onSetTime ? "Tap to enter custom time" : undefined}
            data-testid="timer-display"
          >{fmt(secs)}</span>
        )}
        {!disabled && (
        <>
          {!running ? (
            <button onClick={onStart} className="p-1.5 border border-zinc-300 text-zinc-500 hover:bg-zinc-100" title="Start"><Play className="w-4 h-4" /></button>
          ) : (
            <button onClick={onStop} className="p-1.5 border border-zinc-300 text-zinc-500 hover:bg-zinc-100" title="Stop"><Square className="w-4 h-4" /></button>
          )}
          <button onClick={() => setConfirmReset(true)} disabled={running} className="p-1.5 text-zinc-300 hover:text-zinc-500 disabled:opacity-20" title="Reset"><RotateCcw className="w-4 h-4" /></button>
          <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
            <AlertDialogContent className="rounded-none max-w-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset {label || "timer"}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This sets the recorded time back to zero.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onReset} className="rounded-none bg-red-600 hover:bg-red-700">Reset</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
        )}
        {onGuysChange && (
        <>
          <span className="text-[9px] text-zinc-400 ml-auto flex-shrink-0">Guys</span>
          <select value={guys} onChange={(e) => onGuysChange(parseInt(e.target.value))} disabled={disabled}
            className="h-5 text-[10px] border border-zinc-200 rounded-none bg-white px-0.5 w-9 text-center flex-shrink-0">
            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </>
        )}
        {onRemove && !disabled && (
          <button onClick={onRemove} className={`p-0.5 text-zinc-300 hover:text-red-500 flex-shrink-0 ${!onGuysChange ? 'ml-auto' : ''}`} title="Remove stopwatch" data-testid="timer-remove-btn"><X className="w-3 h-3" /></button>
        )}
      </div>
    </div>
  );
}

function OrderCard({ order, onUpdate, onDelete, onComplete, onAttachFile, onAddToCutList, onAddCalcToCutList, tick, palletTypes, readOnly }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDoneConfirm, setShowDoneConfirm] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showAddToCutListConfirm, setShowAddToCutListConfirm] = useState(false);
  const [removeFileIdx, setRemoveFileIdx] = useState(null);
  const [removePalletIdx, setRemovePalletIdx] = useState(null);
  const isDone = order.done;
  // "Materials All Cut Out" is now automatic: all CUT checkboxes checked
  const _itemsForCut = getPalletItems(order);
  const materialsCut = _itemsForCut.length > 0 && _itemsForCut.every(it => it.cut);
  const aRunning = !!order.assembly_started_at;
  const aSecs = getElapsed(order.assembly_elapsed, order.assembly_started_at);
  const swList = order.stopwatches || [];
  const swSecs = (sw) => getElapsed(sw.elapsed || 0, sw.started_at);
  const totalSecs = aSecs + swList.reduce((sum, sw) => sum + swSecs(sw), 0);
  const runningSws = swList.filter(sw => sw.started_at);
  const [removeSwId, setRemoveSwId] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);

  const openPalletDetails = (item) => {
    const pt = (palletTypes || []).find(p => p.name === item.pallet_type_name);
    if (!pt) { toast.error("Pallet type not found"); return; }
    setDetailsItem({ pallet: pt, qty: item.quantity || 0 });
  };

  const updateSw = (swId, fields) => {
    onUpdate(order.id, { stopwatches: swList.map(s => s.id === swId ? { ...s, ...fields } : s) });
  };
  const addSw = () => {
    if (swList.length >= 15) { toast.error("Maximum of 15 timers per order"); return; }
    const n = swList.length + 1;
    onUpdate(order.id, { stopwatches: [...swList, { id: crypto.randomUUID(), title: `Timer ${n}`, elapsed: 0, started_at: null, guys: 1 }] });
  };

  // Auto-stop any timer that hits the 60-hour cap
  useEffect(() => {
    if (aRunning && aSecs >= MAX_TIMER_SECONDS) {
      onUpdate(order.id, { assembly_elapsed: MAX_TIMER_SECONDS, assembly_started_at: null });
    }
    if (swList.some(sw => sw.started_at && swSecs(sw) >= MAX_TIMER_SECONDS)) {
      onUpdate(order.id, { stopwatches: swList.map(s => s.started_at && swSecs(s) >= MAX_TIMER_SECONDS ? { ...s, elapsed: MAX_TIMER_SECONDS, started_at: null } : s) });
    }
  });

  const startEdit = () => {
    const items = getPalletItems(order);
    setEditForm({
      customer_name: order.customer_name || "",
      pallet_items: items.length > 0 ? items.map(i => ({ ...i })) : [{ pallet_type_name: "", quantity: "" }],
      load_date: order.load_date || "", deliver_date: order.deliver_date || "",
      load_time: order.load_time || "", deliver_note: order.deliver_note || "",
    });
    setEditing(true);
  };
  const saveEdit = () => {
    const cleaned = (editForm.pallet_items || []).filter(i => i.pallet_type_name).map(i => ({ pallet_type_name: i.pallet_type_name, quantity: parseInt(i.quantity) || 0, checked: !!i.checked, cut: !!i.cut }));
    const wasRevised = (() => {
      const key = (arr) => arr.map(i => `${i.pallet_type_name}|${parseInt(i.quantity) || 0}`).sort().join(',');
      return key(getPalletItems(order)) !== key(cleaned)
        || (editForm.load_date || "") !== (order.load_date || "")
        || (editForm.deliver_date || "") !== (order.deliver_date || "");
    })();
    const revisionNote = (() => {
      if (!wasRevised) return "";
      const fd = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const parts = [];
      if ((editForm.load_date || "") !== (order.load_date || "")) {
        if (!order.load_date) parts.push(`load date set to ${fd(editForm.load_date)}`);
        else if (!editForm.load_date) parts.push(`load date removed`);
        else parts.push(`load date changed from ${fd(order.load_date)} to ${fd(editForm.load_date)}`);
      }
      if ((editForm.deliver_date || "") !== (order.deliver_date || "")) {
        if (!order.deliver_date) parts.push(`delivery date set to ${fd(editForm.deliver_date)}`);
        else if (!editForm.deliver_date) parts.push(`delivery date removed`);
        else parts.push(`delivery date changed from ${fd(order.deliver_date)} to ${fd(editForm.deliver_date)}`);
      }
      const oldQ = {}; getPalletItems(order).forEach(i => { if (i.pallet_type_name) oldQ[i.pallet_type_name] = (oldQ[i.pallet_type_name] || 0) + (parseInt(i.quantity) || 0); });
      const newQ = {}; cleaned.forEach(i => { newQ[i.pallet_type_name] = (newQ[i.pallet_type_name] || 0) + i.quantity; });
      Object.keys(newQ).forEach(n => {
        if (!(n in oldQ)) parts.push(`added ${n} x${fmtNum(newQ[n])}`);
        else if (oldQ[n] !== newQ[n]) parts.push(`${n} changed from x${fmtNum(oldQ[n])} to x${fmtNum(newQ[n])}`);
      });
      Object.keys(oldQ).forEach(n => { if (!(n in newQ)) parts.push(`removed ${n}`); });
      if (parts.length === 0) return "";
      const s = parts.join('...');
      return s.charAt(0).toUpperCase() + s.slice(1);
    })();
    onUpdate(order.id, {
      customer_name: editForm.customer_name,
      load_date: editForm.load_date, deliver_date: editForm.deliver_date,
      load_time: editForm.load_time || "", deliver_note: (editForm.deliver_note || "").slice(0, 15),
      pallet_items: cleaned,
      pallet_items_updated_at: new Date().toISOString(),
      pallet_type_name: cleaned[0]?.pallet_type_name || "",
      quantity: cleaned[0]?.quantity || 0,
      ...(wasRevised ? { revised: true } : {}),
      ...(revisionNote ? { revision_notes: [...(order.revision_notes || []), revisionNote] } : {}),
    });
    setEditing(false);
  };
  const confirmDone = () => {
    const u = { done: true, done_at: new Date().toISOString() };
    if (aRunning) { u.assembly_elapsed = aSecs; u.assembly_started_at = null; }
    if (runningSws.length > 0) u.stopwatches = swList.map(s => s.started_at ? { ...s, elapsed: swSecs(s), started_at: null } : s);
    onUpdate(order.id, u);
    setShowDoneConfirm(false);
  };

  const files = order.files || (order.file_url ? [{ url: order.file_url, name: order.file_name }] : []);
  const hasFiles = files.length > 0;
  const [viewImage, setViewImage] = useState(null);
  const [queuedPhotos, setQueuedPhotos] = useState([]);

  useEffect(() => {
    let urls = [];
    let alive = true;
    const load = async () => {
      try {
        const q = await getQueuedPhotos(order.id);
        urls.forEach(u => URL.revokeObjectURL(u.url));
        urls = q.map(p => ({ id: p.id, name: p.name, url: URL.createObjectURL(p.blob), isImg: (p.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(p.name || '') }));
        if (alive) setQueuedPhotos(urls);
      } catch {}
    };
    load();
    const h = () => load();
    window.addEventListener('photo-queue-changed', h);
    return () => { alive = false; window.removeEventListener('photo-queue-changed', h); urls.forEach(u => URL.revokeObjectURL(u.url)); };
  }, [order.id]);

  const printOrder = () => {
    const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const w = window.open('', '_blank');
    if (!w) return;
    const items = getPalletItems(order);
    const fmtD = (ds) => ds ? new Date(ds + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const timerRows = [
      `<tr><td>${esc(order.assembly_label || 'Assembly')}${order.assembly_guys > 1 ? ` (${order.assembly_guys} guys)` : ''}</td><td>${fmt(aSecs)}</td></tr>`,
      ...swList.map(sw => `<tr><td>${esc(sw.title || 'Timer')}${(sw.guys || 1) > 1 ? ` (${sw.guys} guys)` : ''}</td><td>${fmt(swSecs(sw))}</td></tr>`),
    ].join('');
    const html = `<!DOCTYPE html><html><head><title>Order — ${esc(order.customer_name || 'Unnamed')}</title>
<style>
  body { font-family: Arial, sans-serif; padding: 20px; color: #111; max-width: 700px; margin: 0 auto; }
  h1 { font-size: 26px; font-weight: 800; text-align: center; margin: 0 0 4px; }
  .badges { text-align: center; margin-bottom: 4px; }
  .date { font-size: 10px; color: #888; text-align: right; margin: 0 0 10px; }
  .badge { display: inline-block; font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; padding: 2px 8px; border: 1px solid #999; margin: 0 4px; vertical-align: middle; }
  h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #666; margin: 18px 0 6px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; font-size: 13px; }
  th { background: #222; color: #fff; font-weight: bold; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; text-align: center; }
  .dates { text-align: center; font-size: 15px; font-weight: bold; margin: 2px 0 6px; }
  .dates span { font-size: 10px; font-weight: normal; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-right: 4px; }
  .cb { font-size: 18px; text-align: center; width: 42px; }
  .center { text-align: center; }
  .timeslot { width: 100px; }
  .notes { font-size: 13px; white-space: pre-wrap; border: 1px solid #ccc; padding: 10px; background: #fafafa; margin-bottom: 8px; }
  .nline { border-bottom: 1px solid #999; height: 32px; }
  .total-row td { font-weight: bold; background: #f0f0f0; }
  .action-bar { display: flex; gap: 10px; justify-content: center; margin: 24px auto; }
  .action-btn { padding: 12px 32px; border: none; font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; }
  .print-btn { background: #ea580c; color: #fff; }
  .close-btn { background: #222; color: #fff; }
  @media print { body { padding: 0; } .action-bar { display: none; } }
</style></head><body>
<h1>${esc(order.customer_name || 'Unnamed')}</h1>
${(isDone || materialsCut) ? `<div class="badges">${isDone ? '<span class="badge">Finished / Ready to Pickup</span>' : ''}${materialsCut ? '<span class="badge">Materials All Cut Out</span>' : ''}</div>` : ''}
<div class="dates"><span>Load at Shop</span>${fmtD(order.load_date)} &nbsp;&nbsp;&nbsp; <span>Deliver to Customer</span>${fmtD(order.deliver_date)}</div>
<p class="date">Printed ${new Date().toLocaleDateString()}</p>
<h3>Pallets</h3>
${items.length > 0 ? `<table><thead><tr><th>Done</th><th>Pallet Type</th><th>Qty</th><th>Time</th><th>Cut</th></tr></thead><tbody>${items.map(i => `<tr><td class="cb">${i.checked ? '&#9745;' : '&#9744;'}</td><td class="center">${esc(i.pallet_type_name)}</td><td class="center">${(i.quantity || 0).toLocaleString()}</td><td class="timeslot"></td><td class="cb">${i.cut ? '&#9745;' : '&#9744;'}</td></tr>`).join('')}</tbody></table>` : '<p style="font-size:13px;color:#888;">No pallet types on this order</p>'}
<h3>Time</h3>
<table><thead><tr><th>Timer</th><th>Time</th></tr></thead><tbody>${timerRows}<tr class="total-row"><td>Total</td><td>${fmt(totalSecs)}</td></tr></tbody></table>
<h3>Notes</h3>
${order.notes ? `<div class="notes">${esc(order.notes)}</div>` : ''}
<div class="nline"></div><div class="nline"></div><div class="nline"></div><div class="nline"></div><div class="nline"></div><div class="nline"></div>
${files.length > 0 ? `<h3>Attached Files (${files.length})</h3><table><tbody>${files.map(f => `<tr><td>${esc(f.name || 'File')}</td></tr>`).join('')}</tbody></table>` : ''}
<div class="action-bar">
  <button class="action-btn print-btn" onclick="window.print()">Print</button>
  <button class="action-btn close-btn" onclick="window.close()">Close</button>
</div>
</body></html>`;
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const confirmRemoveFile = () => {
    if (removeFileIdx === null) return;
    const updated = files.filter((_, i) => i !== removeFileIdx);
    onUpdate(order.id, { files: updated });
    setRemoveFileIdx(null);
  };

  return (
    <>
    <div className={`border-2 transition-all ${isDone ? 'border-zinc-300 bg-zinc-50' : 'border-zinc-400 bg-white'}`} data-testid={`order-${order.id}`}>
      {/* Header — customer name + dates + activity */}
      <div className="flex items-center px-3 py-2 gap-2">
        <div className={`w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center transition-colors ${readOnly ? '' : 'cursor-pointer'} ${isDone ? 'bg-zinc-400 border-zinc-400 hover:bg-zinc-300' : 'border-zinc-400 hover:border-zinc-600'}`}
          onClick={readOnly ? undefined : () => isDone ? onUpdate(order.id, { done: false, done_at: null }) : setShowDoneConfirm(true)} data-testid={`order-done-${order.id}`}>
          {isDone && <Check className="w-3.5 h-3.5 text-white" />}
        </div>
        <button onClick={() => setExpanded(!expanded)} className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-1.5 flex-wrap">
            <ChevronRight className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            <span className={`text-xs uppercase tracking-[0.15em] font-bold ${isDone ? 'text-zinc-400 line-through' : 'text-zinc-700'}`} style={{ fontFamily: "Chivo, sans-serif" }}>
              {order.customer_name || 'Unnamed'}{order.revised ? ' (Revised)' : ''}
            </span>
          </div>
          {/* Dates + info line — load first, deliver stacked underneath */}
          <div className="mt-0.5 text-[10px] text-zinc-500 space-y-0.5" style={{ marginLeft: '22px' }}>
            {order.load_date && (
              <div>Load: <b>{fmtDate(order.load_date)}</b>{order.load_time ? <b> {fmtT(order.load_time)}</b> : null}</div>
            )}
            {order.deliver_date && (
              <div>Deliver: <b>{fmtDate(order.deliver_date)}</b>{order.deliver_note ? <span className="text-zinc-400"> {order.deliver_note}</span> : null}</div>
            )}
          </div>
          {/* Status + timer badges — stacked below the dates */}
          {((materialsCut && !isDone) || isDone || aRunning || runningSws.length > 0) && (
            <div className="flex flex-col items-start gap-1 mt-0.5" style={{ marginLeft: '22px' }}>
              {materialsCut && !isDone && (
                <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 px-1 py-px">
                  Materials All Cut Out
                </span>
              )}
              {(aRunning || runningSws.length > 0) && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {aRunning && (
                    <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 px-1 py-px flex items-center gap-1">
                      <span className="relative w-1.5 h-1.5"><span className="absolute inset-0 rounded-full bg-zinc-400 animate-ping opacity-75" /><span className="relative block w-1.5 h-1.5 rounded-full bg-zinc-500" /></span>
                      {order.assembly_label || 'Assembly'}
                    </span>
                  )}
                  {runningSws.map(sw => (
                    <span key={sw.id} className="text-[8px] font-bold uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 px-1 py-px flex items-center gap-1">
                      <span className="relative w-1.5 h-1.5"><span className="absolute inset-0 rounded-full bg-zinc-400 animate-ping opacity-75" /><span className="relative block w-1.5 h-1.5 rounded-full bg-zinc-500" /></span>
                      {sw.title || 'Timer'}
                    </span>
                  ))}
                </div>
              )}
              {isDone && <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-400 bg-zinc-100 border border-zinc-200 px-1 py-px">Finished/Ready to Pickup</span>}
            </div>
          )}
        </button>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {hasFiles && (
            <span className="text-[9px] text-zinc-400 mr-0.5">{files.length}</span>
          )}
          {!readOnly && (
          <>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button className="p-2 hover:bg-zinc-100" data-testid={`order-menu-${order.id}`}><MoreVertical className="w-5 h-5 text-zinc-400" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-zinc-300 min-w-[200px]">
              {!isDone && (
              <DropdownMenuItem onClick={() => { setExpanded(true); startEdit(); }} className="rounded-none text-sm py-2.5 cursor-pointer" data-testid={`order-edit-${order.id}`}>
                <Edit2 className="w-4 h-4 mr-2" /> Edit Order
              </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setShowAddToCutListConfirm(true)} className="rounded-none text-sm py-2.5 cursor-pointer" data-testid={`order-add-to-cutlist-${order.id}`}>
                <ListPlus className="w-4 h-4 mr-2" /> Add To Cut List
              </DropdownMenuItem>
              <DropdownMenuItem onClick={printOrder} className="rounded-none text-sm py-2.5 cursor-pointer" data-testid={`order-print-${order.id}`}>
                <Printer className="w-4 h-4 mr-2" /> Print Order
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowCompleteConfirm(true)} className="rounded-none text-sm py-2.5 cursor-pointer">
                <Archive className="w-4 h-4 mr-2" /> Complete Order
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(order.id)} className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded-none text-sm py-2.5 cursor-pointer">
                <Trash2 className="w-4 h-4 mr-2" /> Delete Order
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AlertDialog open={showAddToCutListConfirm} onOpenChange={setShowAddToCutListConfirm}>
            <AlertDialogContent className="rounded-none max-w-sm" data-testid={`order-add-cutlist-confirm-${order.id}`}>
              <AlertDialogHeader>
                <AlertDialogTitle>Add to Cut List?</AlertDialogTitle>
                <AlertDialogDescription>
                  {(() => {
                    const items = getPalletItems(order);
                    const checkedNames = items.filter(i => i.checked || i.cut).map(i => i.pallet_type_name);
                    const remaining = items.length - checkedNames.length;
                    const who = order.customer_name ? `${order.customer_name}'s` : "this order's";
                    if (checkedNames.length === 0) return `This adds all of ${who} pallets to your cut list.`;
                    if (remaining === 0) return "All pallet types on this order are already checked off — nothing will be added.";
                    return `This adds ${who} unchecked pallets to your cut list. Already checked off — will be skipped: ${checkedNames.join(', ')}.`;
                  })()}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-none border-zinc-300" data-testid={`order-add-cutlist-cancel-${order.id}`}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onAddToCutList && onAddToCutList(order)} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid={`order-add-cutlist-confirm-btn-${order.id}`}>
                  Add to Cut List
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </>
          )}
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
      <div className="px-3 pb-3 border-t border-zinc-100 pt-3">
        {/* Edit mode */}
        {editing ? (
          <div className="space-y-2 mb-3">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Customer</p>
              <Input value={editForm.customer_name} onChange={e => setEditForm(p => ({ ...p, customer_name: e.target.value }))} className="rounded-none border-zinc-300 h-7 text-xs" />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Pallet Types</p>
              {(editForm.pallet_items || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-1 mb-1 min-w-0">
                  <select value={item.pallet_type_name} onChange={e => {
                    const items = [...editForm.pallet_items]; items[idx] = { ...items[idx], pallet_type_name: e.target.value };
                    setEditForm(p => ({ ...p, pallet_items: items }));
                  }} className="flex-1 min-w-0 h-7 border border-zinc-300 rounded-none text-xs px-1 bg-white">
                    <option value="">— Select —</option>
                    {(palletTypes || []).map(pt => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                  </select>
                  <Input type="number" placeholder="#" value={item.quantity} onChange={e => {
                    const items = [...editForm.pallet_items]; items[idx] = { ...items[idx], quantity: e.target.value.slice(0, 6) };
                    setEditForm(p => ({ ...p, pallet_items: items }));
                  }} className="w-14 flex-shrink-0 rounded-none border-zinc-300 h-7 text-xs" />
                  {editForm.pallet_items.length > 1 && (
                    <button onClick={() => setRemovePalletIdx(idx)}
                      className="p-1 flex-shrink-0 text-zinc-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              ))}
              <button onClick={() => setEditForm(p => ({ ...p, pallet_items: [...(p.pallet_items || []), { pallet_type_name: "", quantity: "" }] }))}
                className="text-[10px] text-zinc-400 hover:text-zinc-600 flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Add pallet type</button>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Load at Shop</p>
                <Input type="date" value={editForm.load_date} onClick={openPicker} onChange={e => setEditForm(p => ({ ...p, load_date: e.target.value }))} className="rounded-none border-zinc-300 h-7 text-xs" />
                <Input type="time" value={editForm.load_time} onClick={openPicker} onChange={e => setEditForm(p => ({ ...p, load_time: e.target.value }))} className="rounded-none border-zinc-300 h-7 text-xs mt-1" data-testid="edit-load-time-input" />
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-0.5">Deliver to Customer</p>
                <Input type="date" value={editForm.deliver_date} onClick={openPicker} onChange={e => setEditForm(p => ({ ...p, deliver_date: e.target.value }))} className="rounded-none border-zinc-300 h-7 text-xs" />
                <Input value={editForm.deliver_note} maxLength={15} onChange={e => setEditForm(p => ({ ...p, deliver_note: e.target.value.slice(0, 15) }))} className="rounded-none border-zinc-300 h-7 text-xs mt-1" data-testid="edit-deliver-note-input" />
              </div>
            </div>
            <div className="flex justify-end gap-1">
              <button onClick={saveEdit} className="px-3 py-1 text-xs font-bold bg-zinc-800 text-white hover:bg-zinc-900">Save</button>
              <button onClick={() => setEditing(false)} className="px-3 py-1 text-xs text-zinc-500 hover:text-zinc-700">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="mb-2">
            {(order.revision_notes || []).length > 0 && (
              <div className="mb-2 p-2 bg-amber-50 border border-amber-200 space-y-1" data-testid={`order-revisions-${order.id}`}>
                {(order.revision_notes || []).map((n, i) => (
                  <p key={i} className="text-[11px] text-amber-800 leading-snug [overflow-wrap:anywhere]"><b>Revision:</b> {n}</p>
                ))}
              </div>
            )}
            <div className="text-sm space-y-1.5">
              {getPalletItems(order).length > 0 && !isDone && !readOnly && (
                <div className="flex items-center gap-2" data-testid={`order-pallet-labels-${order.id}`}>
                  <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-400 leading-none whitespace-nowrap w-5 flex-shrink-0">Done</span>
                  <span className="flex-1" />
                  <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-400 leading-none whitespace-nowrap text-right flex-shrink-0">Cut</span>
                </div>
              )}
              {getPalletItems(order).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  {!isDone && !readOnly && (
                    <div
                      className={`w-5 h-5 flex-shrink-0 border flex items-center justify-center cursor-pointer transition-colors ${
                        item.checked ? 'bg-zinc-500 border-zinc-500' : 'border-zinc-300 hover:border-zinc-500'
                      }`}
                      onClick={() => {
                        const items = getPalletItems(order).map((it, i) => i === idx ? { ...it, checked: !it.checked } : it);
                        onUpdate(order.id, { pallet_items: items, pallet_items_updated_at: new Date().toISOString() });
                      }}
                      data-testid={`order-pallet-done-${order.id}-${idx}`}
                    >
                      {item.checked && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                  )}
                  <span className={`font-semibold [overflow-wrap:anywhere] cursor-pointer hover:text-orange-600 transition-colors ${item.checked ? 'line-through text-zinc-400' : 'text-zinc-700'}`}
                    onClick={() => openPalletDetails(item)} data-testid={`order-pallet-details-${order.id}-${idx}`}>
                    {item.pallet_type_name}
                    {item.quantity > 0 && <span className={`font-normal whitespace-nowrap ${item.checked ? 'text-zinc-300' : 'text-zinc-400'}`}>{' '}x{fmtNum(item.quantity)}</span>}
                  </span>
                  <span className="flex-1" />
                  {!isDone && !readOnly && (
                    <div
                      className={`w-5 h-5 flex-shrink-0 border flex items-center justify-center cursor-pointer transition-colors ${item.cut ? 'bg-violet-500 border-violet-500' : 'border-zinc-300 hover:border-violet-400'}`}
                      onClick={() => {
                        const items = getPalletItems(order).map((it, i) => i === idx ? { ...it, cut: !it.cut } : it);
                        onUpdate(order.id, { pallet_items: items, pallet_items_updated_at: new Date().toISOString() });
                      }}
                      data-testid={`order-pallet-cut-${order.id}-${idx}`}
                    >
                      {item.cut && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                  )}
                </div>
              ))}
              {getPalletItems(order).length === 0 && (
                <span className="text-zinc-400">No pallet types added</span>
              )}
            </div>
          </div>
        )}

        {/* Files */}
        <div className="space-y-2 mb-2">
          {files.map((f, fi) => {
            const isImg = /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(f.name || '');
            return (
              <div key={fi} className="relative group">
                {isImg ? (
                  <button type="button" onClick={() => setViewImage({ url: resolveImageUrl(f.url), name: f.name })} className="block w-full" data-testid={`order-photo-${order.id}-${fi}`}>
                    <img src={resolveImageUrl(f.url)} alt={f.name} className="w-full max-h-48 object-contain border border-zinc-200 bg-zinc-50" />
                  </button>
                ) : (
                  <a href={resolveImageUrl(f.url)} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2.5 border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 transition-colors">
                    <FileText className="w-6 h-6 text-zinc-400 flex-shrink-0" />
                    <span className="text-xs font-semibold text-zinc-700 truncate flex-1">{f.name}</span>
                    <Download className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                  </a>
                )}
                {!isDone && !readOnly && (
                  <button onClick={() => setRemoveFileIdx(fi)}
                    className="absolute top-1 right-1 p-1 bg-white/80 border border-zinc-300 hover:bg-red-50 hover:border-red-300 transition-colors"
                    title="Remove file">
                    <X className="w-3 h-3 text-zinc-500" />
                  </button>
                )}
              </div>
            );
          })}
          {/* Photos taken offline — stored on this device, upload automatically */}
          {queuedPhotos.map((p) => (
            <div key={p.id} className="relative" data-testid={`order-pending-photo-${order.id}`}>
              {p.isImg ? (
                <button type="button" onClick={() => setViewImage({ url: p.url, name: p.name })} className="block w-full">
                  <img src={p.url} alt={p.name} className="w-full max-h-48 object-contain border border-amber-300 bg-amber-50/40" />
                </button>
              ) : (
                <div className="flex items-center gap-3 p-2.5 border border-amber-300 bg-amber-50/40">
                  <FileText className="w-6 h-6 text-amber-500 flex-shrink-0" />
                  <span className="text-xs font-semibold text-zinc-700 truncate flex-1">{p.name}</span>
                </div>
              )}
              <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-500 text-white">Waiting to upload</span>
            </div>
          ))}
        </div>
        {!isDone && !readOnly && editing && (
          <label className="flex items-center justify-center gap-2 mb-2 p-3 border-2 border-dashed border-zinc-200 cursor-pointer hover:bg-zinc-50 transition-colors">
            <Upload className="w-4 h-4 text-zinc-400" />
            <span className="text-xs text-zinc-400 font-medium">Add file...</span>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" multiple
              onChange={(e) => { if (e.target.files?.length) onAttachFile(order.id, Array.from(e.target.files)); }} />
          </label>
        )}

        {/* Timers */}
        <div className="border-t border-zinc-100 pt-2 divide-y divide-zinc-100">
          <TimerRow label={order.assembly_label || "Assembly"} secs={aSecs} running={aRunning} guys={order.assembly_guys || 1} disabled={isDone || readOnly}
            onRename={(name) => onUpdate(order.id, { assembly_label: name })}
            onGuysChange={(n) => onUpdate(order.id, { assembly_guys: n })}
            onStart={() => onUpdate(order.id, { assembly_started_at: new Date().toISOString(), assembly_elapsed: order.assembly_elapsed || 0 })}
            onStop={() => onUpdate(order.id, { assembly_elapsed: aSecs, assembly_started_at: null })}
            onReset={() => onUpdate(order.id, { assembly_elapsed: 0, assembly_started_at: null })}
            onSetTime={(secs) => onUpdate(order.id, { assembly_elapsed: secs, assembly_started_at: null })} />
          {swList.map(sw => (
            <TimerRow key={sw.id} label={sw.title || 'Timer'} secs={swSecs(sw)} running={!!sw.started_at} disabled={isDone || readOnly}
              testId={`order-sw-${order.id}-${sw.id}`}
              guys={sw.guys || 1}
              onGuysChange={(n) => updateSw(sw.id, { guys: n })}
              onRename={(name) => updateSw(sw.id, { title: name })}
              onRemove={() => setRemoveSwId(sw.id)}
              onStart={() => updateSw(sw.id, { started_at: new Date().toISOString(), elapsed: sw.elapsed || 0 })}
              onStop={() => updateSw(sw.id, { elapsed: swSecs(sw), started_at: null })}
              onReset={() => updateSw(sw.id, { elapsed: 0, started_at: null })}
              onSetTime={(secs) => updateSw(sw.id, { elapsed: secs, started_at: null })} />
          ))}
          {!isDone && !readOnly && (
            <button onClick={addSw} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-zinc-600 py-1 transition-colors" data-testid={`order-add-stopwatch-${order.id}`}>
              <Plus className="w-3 h-3" /> Add Stopwatch
            </button>
          )}
          {(swList.length > 0 || totalSecs > 0) && (
            <div className="flex items-center gap-2 py-1 border-t border-zinc-100 mt-1" data-testid={`order-total-time-${order.id}`}>
              <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold w-16 flex-shrink-0">Total</span>
              <span className="text-sm font-mono font-bold text-zinc-900">{fmt(totalSecs)}</span>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mt-2">
          <NoteArea defaultValue={order.notes || ""}
            onBlur={(e) => { if (e.target.value !== (order.notes || "")) onUpdate(order.id, { notes: e.target.value }); }}
            placeholder="Add notes..." rows={order.notes ? Math.min(Math.ceil(order.notes.length / 40) + 1, 4) : 1}
            className="text-xs border-zinc-200 rounded-none resize-none min-h-[28px] focus-visible:ring-1 focus-visible:ring-zinc-400"
            testId={`order-notes-${order.id}`}
            disabled={readOnly} />
        </div>
      </div>
      )}
    </div>

    {/* Full-screen photo viewer (works in PWA on iPhone/Android/tablet — no new tab needed) */}
    {viewImage && (
      <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-3" onClick={() => setViewImage(null)} data-testid="order-photo-lightbox">
        <ZoomableImage src={viewImage.url} alt={viewImage.name || 'Order photo'} className="max-w-full max-h-[90vh] object-contain" />
        <button onClick={() => setViewImage(null)} className="absolute top-3 right-3 p-2.5 bg-white/15 text-white hover:bg-white/30 transition-colors" data-testid="order-photo-lightbox-close">
          <X className="w-6 h-6" />
        </button>
        {viewImage.name && <p className="absolute bottom-3 left-0 right-0 text-center text-xs text-white/60 px-4 truncate">{viewImage.name}</p>}
      </div>
    )}

    {/* Remove stopwatch confirmation */}
    <AlertDialog open={!!removeSwId} onOpenChange={(open) => { if (!open) setRemoveSwId(null); }}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove Stopwatch?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            This removes <strong>{swList.find(s => s.id === removeSwId)?.title || 'this stopwatch'}</strong> and its recorded time from the order.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { onUpdate(order.id, { stopwatches: swList.filter(s => s.id !== removeSwId) }); setRemoveSwId(null); }} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="remove-stopwatch-confirm">Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>


    {/* Done confirmation */}
    <AlertDialog open={showDoneConfirm} onOpenChange={setShowDoneConfirm}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Mark as Finished?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            This will mark <strong>{order.customer_name}</strong> as Finished/Ready to Pickup.{aRunning ? ' The assembly timer will be stopped.' : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmDone} className="bg-zinc-800 hover:bg-zinc-900 text-white rounded-none">Mark Finished</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Complete order confirmation */}
    <AlertDialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Complete Order?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            This will move <strong>{order.customer_name}</strong> to Order History. This cannot be undone from here.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { onComplete(order.id); setShowCompleteConfirm(false); }} className="bg-zinc-800 hover:bg-zinc-900 text-white rounded-none">Complete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Pallet type details + calculated results */}
    {detailsItem && (
      <OrderPalletDetailsDialog pallet={detailsItem.pallet} qty={detailsItem.qty} onClose={() => setDetailsItem(null)}
        onAddToCutList={onAddCalcToCutList} hideAddToList={readOnly} />
    )}

    {/* Remove file confirmation */}
    <AlertDialog open={removeFileIdx !== null} onOpenChange={() => setRemoveFileIdx(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove File?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            Are you sure you want to remove {removeFileIdx !== null && files[removeFileIdx] ? <strong>{files[removeFileIdx].name}</strong> : 'this file'}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmRemoveFile} className="bg-red-600 hover:bg-red-700 text-white rounded-none">Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Remove pallet type confirmation */}
    <AlertDialog open={removePalletIdx !== null} onOpenChange={() => setRemovePalletIdx(null)}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove Pallet Type?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            Are you sure you want to remove {removePalletIdx !== null && editForm.pallet_items?.[removePalletIdx]?.pallet_type_name ? <strong>{editForm.pallet_items[removePalletIdx].pallet_type_name}</strong> : 'this pallet type'} from the order?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { const items = (editForm.pallet_items || []).filter((_, i) => i !== removePalletIdx); setEditForm(p => ({ ...p, pallet_items: items })); setRemovePalletIdx(null); }} className="bg-red-600 hover:bg-red-700 text-white rounded-none">Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export function Orders({ palletTypes, readOnly, onAddToCutList, onAddCalcToCutList }) {
  const [orders, setOrders] = useState(() => {
    // Open instantly with the local copy — server refresh happens in background
    try { return JSON.parse(localStorage.getItem('orders_cache') || '[]'); } catch { return []; }
  });
  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [orderTab, setOrderTab] = useState('active');
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState({
    customer_name: "", load_date: "", deliver_date: "", load_time: "", deliver_note: "", is_event: false,
    pallet_items: [{ pallet_type_name: "", quantity: "" }],
    assembly_guys: 1, cut_guys: 1, notes: "",
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removeNewPalletIdx, setRemoveNewPalletIdx] = useState(null);
  const pendingOrderUpdates = useRef((() => {
    // Survive app close: pending order changes are persisted on the device
    try { return JSON.parse(localStorage.getItem('pending_order_updates') || '{}'); } catch { return {}; }
  })());
  const persistPendingOrders = () => {
    try { localStorage.setItem('pending_order_updates', JSON.stringify(pendingOrderUpdates.current)); } catch {}
  };
  const setPendingOrderUpdate = (id, fields) => {
    pendingOrderUpdates.current[id] = { ...pendingOrderUpdates.current[id], ...fields };
    persistPendingOrders();
  };

  const fetchOrders = useCallback(async () => {
    try {
      const { data: serverData } = await axios.get(`${API}/orders`);
      // Merge offline-created orders the server doesn't have; drop offline-deleted ones
      let data = serverData;
      try {
        const deletedIds = getQueuedDeletedIds('orders');
        if (deletedIds.length > 0) data = data.filter(o => !deletedIds.includes(o.id));
        // Hide orders completed on this device whose sync hasn't landed yet
        const completedIds = getQueuedOps('orders').filter(op => op.method === 'put' && op.body?.completed).map(op => op.entityId);
        if (completedIds.length > 0) data = data.filter(o => !completedIds.includes(o.id));
        getQueuedCreates('orders').forEach(body => {
          if (body?.id && !data.find(o => o.id === body.id)) data.unshift(body);
        });
      } catch {}
      // Merge pending timer updates that server may not have
      const pending = pendingOrderUpdates.current;
      data.forEach(o => {
        const p = pending[o.id];
        if (!p) return;
        // Most recent click wins: drop stale local checkbox state if the server copy is newer
        [["pallet_items", "pallet_items_updated_at"], ["materials_cut", "materials_cut_updated_at"]].forEach(([f, ts]) => {
          if (p[ts] && o[ts] && o[ts] > p[ts]) { delete p[f]; delete p[ts]; }
        });
        Object.assign(o, p);
      });
      persistPendingOrders();
      // Also check localStorage for running order timers
      try {
        const localTimers = JSON.parse(localStorage.getItem('order_timers') || '{}');
        data.forEach(o => {
          const local = localTimers[o.id];
          if (!local) return;
          // Local running, server stopped → keep local
          if (local.cut_started_at && !o.cut_started_at) {
            o.cut_started_at = local.cut_started_at; o.cut_elapsed = local.cut_elapsed;
            pendingOrderUpdates.current[o.id] = { ...pendingOrderUpdates.current[o.id], cut_started_at: local.cut_started_at, cut_elapsed: local.cut_elapsed };
          }
          if (local.assembly_started_at && !o.assembly_started_at) {
            o.assembly_started_at = local.assembly_started_at; o.assembly_elapsed = local.assembly_elapsed;
            pendingOrderUpdates.current[o.id] = { ...pendingOrderUpdates.current[o.id], assembly_started_at: local.assembly_started_at, assembly_elapsed: local.assembly_elapsed };
          }
          // Custom stopwatches: local running, server stopped → keep local entry
          if (Array.isArray(local.stopwatches) && local.stopwatches.some(s => s.started_at)) {
            const serverSws = o.stopwatches || [];
            let changed = false;
            const merged = serverSws.map(s => {
              const loc = local.stopwatches.find(l => l.id === s.id);
              if (loc?.started_at && !s.started_at) { changed = true; return { ...s, started_at: loc.started_at, elapsed: loc.elapsed }; }
              return s;
            });
            if (changed) {
              o.stopwatches = merged;
              pendingOrderUpdates.current[o.id] = { ...pendingOrderUpdates.current[o.id], stopwatches: merged };
            }
          }
        });
      } catch {}
      setOrders(data);
    } catch {}
  }, []);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Persist orders locally so they survive restarts and open offline
  useEffect(() => {
    try { localStorage.setItem('orders_cache', JSON.stringify(orders)); } catch {}
  }, [orders]);

  // Save order timer state to localStorage
  useEffect(() => {
    const timers = {};
    orders.forEach(o => {
      if (o.cut_started_at || o.assembly_started_at || o.cut_elapsed > 0 || o.assembly_elapsed > 0 || (o.stopwatches || []).length > 0) {
        timers[o.id] = { cut_started_at: o.cut_started_at, cut_elapsed: o.cut_elapsed, assembly_started_at: o.assembly_started_at, assembly_elapsed: o.assembly_elapsed, stopwatches: o.stopwatches || [] };
      }
    });
    localStorage.setItem('order_timers', JSON.stringify(timers));
  }, [orders]);

  useEffect(() => {
    const hasRunning = orders.some(o => o.assembly_started_at || o.cut_started_at || (o.stopwatches || []).some(s => s.started_at));
    if (!hasRunning) return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [orders]);

  // Flush pending order updates periodically
  useEffect(() => {
    const interval = setInterval(async () => {
      const pending = { ...pendingOrderUpdates.current };
      for (const id of Object.keys(pending)) {
        const payload = pendingOrderUpdates.current[id];
        if (!payload) continue;
        try {
          await axios.put(`${API}/orders/${id}`, payload, { timeout: 15000 });
          if (pendingOrderUpdates.current[id] === payload) { delete pendingOrderUpdates.current[id]; persistPendingOrders(); }
        } catch {}
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleFileUpload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try { const { data } = await axios.post(`${API}/orders/upload`, fd, { timeout: 30000 }); return data; }
    catch { return null; }
    finally { setUploading(false); }
  };

  const [creating, setCreating] = useState(false);
  const handleCreate = async () => {
    if (creating) return; // double-click protection
    if (!form.customer_name.trim()) { toast.error(form.is_event ? "Event name required" : "Customer name required"); return; }
    setCreating(true);
    const cleanedItems = form.is_event ? [] : (form.pallet_items || []).filter(i => i.pallet_type_name).map(i => ({ pallet_type_name: i.pallet_type_name, quantity: parseInt(i.quantity) || 0 }));
    const newOrder = {
      id: crypto.randomUUID(),
      ...form,
      pallet_items: cleanedItems,
      pallet_type_name: cleanedItems[0]?.pallet_type_name || "",
      quantity: cleanedItems[0]?.quantity || 0,
      files: [],
      assembly_elapsed: 0, assembly_started_at: null, stopwatches: [],
      cut_elapsed: 0, cut_started_at: null, done: false, done_at: null,
      created_at: new Date().toISOString(),
    };
    const files = [...pendingFiles];
    // Optimistic: order appears INSTANTLY, form closes — no waiting on the network
    setOrders(prev => [newOrder, ...prev]);
    setForm({ customer_name: "", load_date: "", deliver_date: "", load_time: "", deliver_note: "", is_event: false, pallet_items: [{ pallet_type_name: "", quantity: "" }], assembly_guys: 1, cut_guys: 1, notes: "" });
    setPendingFiles([]); setShowForm(false);
    toast.success(newOrder.is_event ? "Event created" : "Order created");
    try {
      // Write-ahead: order saved on device FIRST, synced immediately when online.
      // Closing the app or a hung request can never lose it or duplicate it.
      queueAndFlush({ method: "post", path: "/orders", body: newOrder, entity: "orders", entityId: newOrder.id });
      // Photos: upload now when possible, otherwise queue them (works fully offline)
      if (files.length > 0) {
        const uploaded = [];
        for (const file of files) {
          let ok = false;
          if (navigator.onLine) {
            const result = await handleFileUpload(file);
            if (result) { uploaded.push({ url: result.url, name: result.file_name }); ok = true; }
          }
          if (!ok) await queuePhoto(newOrder.id, file).catch(() => {});
        }
        if (uploaded.length > 0) {
          setOrders(prev => prev.map(o => o.id === newOrder.id ? { ...o, files: uploaded } : o));
          axios.put(`${API}/orders/${newOrder.id}`, { files: uploaded }, { timeout: 15000 }).catch(() => {
            setPendingOrderUpdate(newOrder.id, { files: uploaded });
          });
        }
      }
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (id, fields) => {
    setOrders(prev => prev.map(o => o.id === id ? { ...o, ...fields } : o));
    // Write-ahead: persisted on device first so the newest value can't be lost
    setPendingOrderUpdate(id, fields);
    const payload = pendingOrderUpdates.current[id];
    try {
      await axios.put(`${API}/orders/${id}`, payload, { timeout: 15000 });
      if (pendingOrderUpdates.current[id] === payload) { delete pendingOrderUpdates.current[id]; persistPendingOrders(); }
    } catch {
      // stays in pending — flushed automatically
    }
  };

  const handleComplete = async (id) => {
    const order = orders.find(o => o.id === id);
    const u = { completed: true, completed_at: new Date().toISOString() };
    if (order?.assembly_started_at) { u.assembly_elapsed = getElapsed(order.assembly_elapsed, order.assembly_started_at); u.assembly_started_at = null; }
    const sws = order?.stopwatches || [];
    if (sws.some(s => s.started_at)) u.stopwatches = sws.map(s => s.started_at ? { ...s, elapsed: getElapsed(s.elapsed || 0, s.started_at), started_at: null } : s);
    setOrders(prev => prev.filter(o => o.id !== id));
    // Write-ahead: saved on device first, then synced — can NEVER be lost or undone by a refetch
    queueAndFlush({ method: "put", path: `/orders/${id}`, body: u, entity: "orders", entityId: id });
    toast.success("Order moved to history");
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setOrders(prev => prev.filter(o => o.id !== deleteId));
    // Write-ahead delete: recorded on device BEFORE any network attempt, so a hung
    // request or closed app can never resurrect the order
    queueAndFlush({ method: "delete", path: `/orders/${deleteId}`, entity: "orders", entityId: deleteId });
    toast.success("Order deleted");
    setDeleteId(null);
  };

  const handleAttachFile = async (orderId, fileList) => {
    const filesToUpload = Array.isArray(fileList) ? fileList : [fileList];
    const uploaded = [];
    let queued = 0;
    for (const file of filesToUpload) {
      let ok = false;
      if (navigator.onLine) {
        const result = await handleFileUpload(file);
        if (result) { uploaded.push({ url: result.url, name: result.file_name }); ok = true; }
      }
      if (!ok) {
        // Offline (or upload failed): keep the photo on the device and sync later
        try { await queuePhoto(orderId, file); queued++; } catch {}
      }
    }
    if (queued > 0) {
      window.dispatchEvent(new CustomEvent("photo-queue-changed"));
      toast.success(`${queued} photo${queued > 1 ? 's' : ''} saved`);
    }
    if (uploaded.length === 0) return;
    const order = orders.find(o => o.id === orderId);
    const existing = order?.files || (order?.file_url ? [{ url: order.file_url, name: order.file_name }] : []);
    const newFiles = [...existing, ...uploaded];
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, files: newFiles } : o));
    try {
      await axios.put(`${API}/orders/${orderId}`, { files: newFiles }, { timeout: 15000 });
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? 's' : ''} attached`);
    } catch {
      setPendingOrderUpdate(orderId, { files: newFiles });
      toast.success("Attached");
    }
  };

  // Refresh instantly when another device changes orders (WebSocket) or queued photos land
  useEffect(() => {
    const h = () => fetchOrders();
    window.addEventListener("photo-queue-flushed", h);
    window.addEventListener("orders-updated", h);
    return () => { window.removeEventListener("photo-queue-flushed", h); window.removeEventListener("orders-updated", h); };
  }, [fetchOrders]);

  const [calendarDate, setCalendarDate] = useState(null);
  const [calField, setCalField] = useState(() => localStorage.getItem('calendar_date_field') === 'deliver' ? 'deliver' : 'load');
  useEffect(() => {
    const h = () => setCalField(localStorage.getItem('calendar_date_field') === 'deliver' ? 'deliver' : 'load');
    window.addEventListener('calendar-setting-changed', h);
    return () => window.removeEventListener('calendar-setting-changed', h);
  }, []);

  // Dates that have orders (for highlighting)
  const orderDates = useMemo(() => {
    const dates = [];
    orders.forEach(o => {
      if (o.is_event) return;
      const d = calField === 'deliver' ? o.deliver_date : o.load_date;
      if (d) dates.push(new Date(d + 'T12:00:00'));
    });
    return dates;
  }, [orders, calField]);

  // Dates that have events (different colored line)
  const eventDates = useMemo(() => {
    const dates = [];
    orders.forEach(o => {
      if (!o.is_event) return;
      const d = calField === 'deliver' ? o.deliver_date : o.load_date;
      if (d) dates.push(new Date(d + 'T12:00:00'));
    });
    return dates;
  }, [orders, calField]);

  // Orders for a selected calendar date
  const ordersForDate = useMemo(() => {
    if (!calendarDate) return [];
    const sel = calendarDate.toISOString().split('T')[0];
    return orders.filter(o => (calField === 'deliver' ? o.deliver_date : o.load_date) === sel);
  }, [calendarDate, orders, calField]);

  // Sort by earliest load_date
  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const aDate = a.load_date || '9999-99-99';
      const bDate = b.load_date || '9999-99-99';
      return aDate.localeCompare(bDate);
    });
  }, [orders]);

  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid="orders-section">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold text-zinc-900 tracking-tight flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <FileText className="w-5 h-5 text-zinc-600" /> Orders
          </h2>
          <span className="text-xs text-zinc-400">({orders.length})</span>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-zinc-400 text-zinc-600 hover:bg-zinc-50 transition-colors"
              data-testid="new-order-btn"><Plus className="w-3.5 h-3.5" /> Create</button>
            <button onClick={() => setShowOrderHistory(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 text-zinc-500 hover:bg-zinc-50 transition-colors"
              data-testid="order-history-btn"><HistoryIcon className="w-3.5 h-3.5" /> History</button>
          </div>
        )}
        {readOnly && (
          <button onClick={() => setShowOrderHistory(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-zinc-300 text-zinc-500 hover:bg-zinc-50 transition-colors"
            data-testid="order-history-btn"><HistoryIcon className="w-3.5 h-3.5" /> History</button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-200 mb-4">
        {[
          { key: 'active', label: 'Active' },
          { key: 'finished', label: 'Finished' },
          { key: 'events', label: 'Events' },
          { key: 'calendar', label: 'Calendar' },
        ].map(t => (
          <button key={t.key} onClick={() => { setOrderTab(t.key); setCalendarDate(null); }}
            className={`px-2 sm:px-4 py-2 text-[11px] font-bold uppercase tracking-wide sm:tracking-wider border-b-2 -mb-px transition-colors whitespace-nowrap ${orderTab === t.key ? 'border-zinc-800 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
            data-testid={`orders-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* Active Tab */}
      {orderTab === 'active' && (
        sortedOrders.filter(o => !o.done && !o.is_event).length === 0 ? (
          <div className="py-4 text-center px-4" data-testid="orders-empty-message">
            <p className="text-sm font-bold text-zinc-600 mb-1">NO ACTIVE ORDERS</p>
            <p className="text-sm text-zinc-400 leading-relaxed">
              To create an order, click CREATE, and add pallet types to the order.
              <br />
              If no pallet types are available, go to the Calculator page and create one.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="active-orders-list">
            {sortedOrders.filter(o => !o.done && !o.is_event).map(order => (
              <OrderCard key={order.id} order={order} tick={tick} palletTypes={palletTypes} readOnly={readOnly} onAddToCutList={onAddToCutList} onAddCalcToCutList={onAddCalcToCutList}
                onUpdate={handleUpdate} onDelete={setDeleteId} onComplete={handleComplete} onAttachFile={handleAttachFile} />
            ))}
          </div>
        )
      )}

      {/* Finished Tab */}
      {orderTab === 'finished' && (
        sortedOrders.filter(o => o.done && !o.is_event).length === 0 ? (
          <div className="py-4 text-center px-4" data-testid="orders-empty-message">
            <p className="text-sm font-bold text-zinc-600 mb-1">NO FINISHED ORDERS</p>
            <p className="text-sm text-zinc-400 leading-relaxed">Orders marked Finished / Ready to Pickup will show here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="finished-orders-list">
            {sortedOrders.filter(o => o.done && !o.is_event).map(order => (
              <OrderCard key={order.id} order={order} tick={tick} palletTypes={palletTypes} readOnly={readOnly} onAddToCutList={onAddToCutList} onAddCalcToCutList={onAddCalcToCutList}
                onUpdate={handleUpdate} onDelete={setDeleteId} onComplete={handleComplete} onAttachFile={handleAttachFile} />
            ))}
          </div>
        )
      )}

      {/* Events Tab */}
      {orderTab === 'events' && (
        sortedOrders.filter(o => o.is_event).length === 0 ? (
          <div className="py-4 text-center px-4" data-testid="orders-empty-message">
            <p className="text-sm font-bold text-zinc-600 mb-1">NO EVENTS</p>
            <p className="text-sm text-zinc-400 leading-relaxed">To create an event, click CREATE and select Event.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="events-list">
            {sortedOrders.filter(o => o.is_event).map(order => (
              <OrderCard key={order.id} order={order} tick={tick} palletTypes={palletTypes} readOnly={readOnly} onAddToCutList={onAddToCutList} onAddCalcToCutList={onAddCalcToCutList}
                onUpdate={handleUpdate} onDelete={setDeleteId} onComplete={handleComplete} onAttachFile={handleAttachFile} />
            ))}
          </div>
        )
      )}

      {/* Calendar Tab */}
      {orderTab === 'calendar' && (
        <div data-testid="order-calendar">
          <div className="flex justify-center border border-zinc-200 p-3">
            <Calendar
              mode="single"
              selected={calendarDate}
              onSelect={setCalendarDate}
              modifiers={{ hasOrder: orderDates, hasEvent: eventDates }}
              modifiersStyles={{
                hasOrder: { fontWeight: 'bold', textDecoration: 'underline', textUnderlineOffset: '3px' },
                hasEvent: { fontWeight: 'bold', backgroundImage: 'linear-gradient(#2563eb,#2563eb)', backgroundSize: '16px 1.5px', backgroundRepeat: 'no-repeat', backgroundPosition: 'center bottom 8px' },
              }}
              classNames={{
                day_today: "bg-accent text-accent-foreground border-2 border-black",
                head_cell: "text-muted-foreground rounded-md w-11 font-normal text-sm",
                day: "h-11 w-11 p-0 font-normal text-base aria-selected:opacity-100 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground",
                caption_label: "text-base font-medium",
                nav_button: "h-8 w-8 bg-transparent p-0 opacity-50 hover:opacity-100 border border-input rounded-md inline-flex items-center justify-center",
              }}
              className="rounded-none"
            />
          </div>
          {calendarDate && (
            <div className="mt-3 pt-3 border-t border-zinc-200">
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-2">
                Orders for {calendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </p>
              {ordersForDate.length === 0 ? (
                <p className="text-xs text-zinc-400 py-2 text-center">No orders on this date</p>
              ) : (
                <div className="space-y-1.5">
                  {ordersForDate.some(o => !o.done && !o.is_event) && (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 pt-1" data-testid="calendar-active-label">Active</p>
                      {ordersForDate.filter(o => !o.done && !o.is_event).map(order => (
                        <OrderCard key={order.id} order={order} tick={tick} palletTypes={palletTypes} readOnly={readOnly} onAddToCutList={onAddToCutList} onAddCalcToCutList={onAddCalcToCutList}
                          onUpdate={handleUpdate} onDelete={setDeleteId} onComplete={handleComplete} onAttachFile={handleAttachFile} />
                      ))}
                    </>
                  )}
                  {ordersForDate.some(o => o.done && !o.is_event) && (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 pt-2" data-testid="calendar-finished-label">Finished / Ready to Pickup</p>
                      {ordersForDate.filter(o => o.done && !o.is_event).map(order => (
                        <OrderCard key={order.id} order={order} tick={tick} palletTypes={palletTypes} readOnly={readOnly} onAddToCutList={onAddToCutList} onAddCalcToCutList={onAddCalcToCutList}
                          onUpdate={handleUpdate} onDelete={setDeleteId} onComplete={handleComplete} onAttachFile={handleAttachFile} />
                      ))}
                    </>
                  )}
                  {ordersForDate.some(o => o.is_event) && (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 pt-2" data-testid="calendar-events-label">Events</p>
                      {ordersForDate.filter(o => o.is_event).map(order => (
                        <OrderCard key={order.id} order={order} tick={tick} palletTypes={palletTypes} readOnly={readOnly} onAddToCutList={onAddToCutList} onAddCalcToCutList={onAddCalcToCutList}
                          onUpdate={handleUpdate} onDelete={setDeleteId} onComplete={handleComplete} onAttachFile={handleAttachFile} />
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Order */}
      <AlertDialog open={showForm} onOpenChange={setShowForm}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none max-w-sm w-[calc(100vw-2rem)] mx-auto max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>New Order</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-3 overflow-x-hidden">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">Type</p>
              <div className="flex gap-1">
                {[[false, 'Order'], [true, 'Event']].map(([val, label]) => (
                  <button key={label} onClick={() => setForm(p => ({ ...p, is_event: val }))}
                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase tracking-wider border transition-colors ${form.is_event === val ? 'bg-zinc-800 text-white border-zinc-800' : 'text-zinc-500 border-zinc-300 hover:bg-zinc-50'}`}
                    data-testid={`order-type-${label.toLowerCase()}`}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">{form.is_event ? 'Event Name *' : 'Customer Name *'}</p>
              <Input value={form.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))}
                placeholder={form.is_event ? 'Event name' : 'Customer name'} className="rounded-none border-zinc-300 h-8 text-sm" data-testid="order-customer-input" />
            </div>
            <div className="space-y-2">
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">{form.is_event ? 'Event Date' : 'Load'}</p>
                <Input type="date" value={form.load_date} onClick={openPicker} onChange={e => setForm(p => ({ ...p, load_date: e.target.value }))}
                  className="rounded-none border-zinc-300 h-8 text-xs w-full" data-testid="order-load-date-input" />
                <Input type="time" value={form.load_time} onClick={openPicker} onChange={e => setForm(p => ({ ...p, load_time: e.target.value }))}
                  className="rounded-none border-zinc-300 h-8 text-xs w-full mt-1" data-testid="order-load-time-input" />
              </div>
              {!form.is_event && (
              <div className="min-w-0">
                <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">Deliver</p>
                <Input type="date" value={form.deliver_date} onClick={openPicker} onChange={e => setForm(p => ({ ...p, deliver_date: e.target.value }))}
                  className="rounded-none border-zinc-300 h-8 text-xs w-full" data-testid="order-deliver-date-input" />
                <Input value={form.deliver_note} maxLength={15} onChange={e => setForm(p => ({ ...p, deliver_note: e.target.value.slice(0, 15) }))}
                  className="rounded-none border-zinc-300 h-8 text-xs w-full mt-1" data-testid="order-deliver-note-input" />
              </div>
              )}
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">Photo</p>
              <div className="space-y-1.5">
                <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-zinc-300 cursor-pointer hover:bg-zinc-50 transition-colors">
                  <CameraIcon className="w-4 h-4 text-zinc-400" />
                  <span className="text-xs text-zinc-500 font-medium">Take Photo</span>
                  <input type="file" className="hidden" accept="image/*" capture="environment"
                    onChange={(e) => setPendingFiles(prev => [...prev, ...Array.from(e.target.files || [])])} data-testid="order-file-input" />
                </label>
                <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-zinc-300 cursor-pointer hover:bg-zinc-50 transition-colors">
                  <Upload className="w-4 h-4 text-zinc-400" />
                  <span className="text-xs text-zinc-500 font-medium">Choose From Gallery{pendingFiles.length > 0 ? ` (${pendingFiles.length} selected)` : ''}</span>
                  <input type="file" className="hidden" accept="image/*" multiple
                    onChange={(e) => setPendingFiles(prev => [...prev, ...Array.from(e.target.files || [])])} data-testid="order-gallery-input" />
                </label>
              </div>
              {pendingFiles.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {pendingFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-zinc-500 px-1">
                      <span className="truncate">{f.name}</span>
                      <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))} className="p-0.5 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {!form.is_event && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">Pallet Types</p>
              {(form.pallet_items || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5 mb-1.5">
                  <select value={item.pallet_type_name} onChange={e => {
                    const items = [...form.pallet_items]; items[idx] = { ...items[idx], pallet_type_name: e.target.value };
                    setForm(p => ({ ...p, pallet_items: items }));
                  }} className="flex-1 min-w-0 h-8 border border-zinc-300 rounded-none text-xs px-1 bg-white" data-testid={`order-pallet-type-${idx}`}>
                    <option value="">— Select —</option>
                    {(palletTypes || []).map(pt => <option key={pt.id} value={pt.name}>{pt.name}</option>)}
                  </select>
                  <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => {
                    const items = [...form.pallet_items]; items[idx] = { ...items[idx], quantity: e.target.value.slice(0, 6) };
                    setForm(p => ({ ...p, pallet_items: items }));
                  }} className="w-14 flex-shrink-0 rounded-none border-zinc-300 h-8 text-xs" data-testid={`order-qty-${idx}`} />
                  {form.pallet_items.length > 1 && (
                    <button onClick={() => setRemoveNewPalletIdx(idx)}
                      className="p-0.5 flex-shrink-0 text-zinc-300 hover:text-red-500" type="button"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setForm(p => ({ ...p, pallet_items: [...(p.pallet_items || []), { pallet_type_name: "", quantity: "" }] }))}
                className="text-[10px] text-zinc-400 hover:text-zinc-600 flex items-center gap-1 mt-0.5" data-testid="add-pallet-item-btn"><Plus className="w-3 h-3" /> Add another pallet type</button>
            </div>
            )}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 mb-1">Notes</p>
              <NoteArea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional notes" rows={2} className="rounded-none border-zinc-300 text-sm resize-none" testId="order-notes-input" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300 text-sm">Cancel</AlertDialogCancel>
            <button onClick={handleCreate} disabled={uploading}
              className="px-4 py-2 bg-zinc-800 text-white text-sm font-bold hover:bg-zinc-900 disabled:opacity-50 transition-colors"
              data-testid="order-save-btn">{uploading ? 'Uploading...' : 'Create'}</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Order History (moved out of Settings) */}
      <OrderHistoryDialog open={showOrderHistory} onOpenChange={setShowOrderHistory} onOrdersChanged={fetchOrders} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delete Order?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">This will permanently delete this order and its attached file.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-none">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove pallet type from new order confirmation */}
      <AlertDialog open={removeNewPalletIdx !== null} onOpenChange={() => setRemoveNewPalletIdx(null)}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove Pallet Type?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              Are you sure you want to remove {removeNewPalletIdx !== null && form.pallet_items?.[removeNewPalletIdx]?.pallet_type_name ? <strong>{form.pallet_items[removeNewPalletIdx].pallet_type_name}</strong> : 'this pallet type'}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setForm(p => ({ ...p, pallet_items: p.pallet_items.filter((_, i) => i !== removeNewPalletIdx) })); setRemoveNewPalletIdx(null); }} className="bg-red-600 hover:bg-red-700 text-white rounded-none">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
